import { useTranslation } from "@sagot/i18n";
import { cn, Icon } from "@sagot/ui";
import type { KeyboardEvent } from "react";
import { useProblems } from "../builder/problems-context";
import type { Definition } from "../types";

const J = "engage.workflows.extras.jumpBar";

interface PathJumpBarProps {
  definition: Definition;
  /** The path last jumped to or worked in; it is marked as the current one. */
  current: string | null;
  onJump: (pathId: string) => void;
  className?: string;
}

/**
 * "A · Welcome", "B · Tuition" ...: one button per path that brings it into view (WF-P5-13), with a warning mark and the
 * words "has problems" on a path that has some. Pressing a path's letter anywhere in the bar jumps straight to it.
 */
export function PathJumpBar({ definition, current, onJump, className }: PathJumpBarProps) {
  const { t } = useTranslation();
  const problems = useProblems();
  const ids = Object.keys(definition.paths).sort();
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) return;
    const id = event.key.toUpperCase();
    if (!(id in definition.paths)) return;
    event.preventDefault();
    onJump(id);
  };
  return (
    <nav aria-label={t(`${J}.label`)} className={cn("rounded-lg border border-line bg-surface", className)} data-testid="path-jump-bar">
      <ul className="flex gap-1 overflow-x-auto p-1">
        {ids.map((id) => {
          const label = definition.paths[id]?.label;
          const flagged = (problems.byPath.get(id)?.length ?? 0) > 0;
          return (
            <li key={id} className="shrink-0">
              <button
                type="button"
                aria-current={current === id ? "true" : undefined}
                data-path={id}
                className={cn(
                  "inline-flex h-8 max-w-48 items-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold",
                  current === id ? "border-primary bg-primary-soft text-primary-ink" : "border-transparent hover:bg-sunken",
                  flagged && "border-warning",
                )}
                onClick={() => { onJump(id); }}
                onKeyDown={onKeyDown}
              >
                <span className="truncate">{label ? `${id} · ${label}` : id}</span>
                {flagged ? (
                  <>
                    <Icon name="alert-triangle" className="size-3.5 shrink-0 text-warning" />
                    <span className="sr-only">{t(`${J}.hasProblems`)}</span>
                  </>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
